import type { ChatMessage, ChatRequest } from './types';

/**
 * De Azure WAF blokkeert elke request-body boven 128 KB met een 403, ongeacht
 * de inhoud. 120 KB laat marge voor headers en afrondingsverschillen.
 */
const MAX_BODY_BYTES = 120_000;

/**
 * Moet gelijk zijn aan `chatHistorySchema.max(...)` in
 * `apps/safezone-api/src/schemas/chat.schema.ts`. Bij veel korte beurten
 * (bijvoorbeeld "ok", "dank je") kan `history.length` deze grens overschrijden
 * terwijl de body nog ruim onder `MAX_BODY_BYTES` blijft — zonder deze check
 * zou de backend die vraag dan alsnog met 400 `invalid_history` afwijzen.
 */
const MAX_HISTORY_MESSAGES = 50;

/**
 * UTF-8-veilige base64. `btoa` alleen breekt op alles buiten latin1, dus
 * accenten en emoji; `String.fromCharCode(...bytes)` zou bij een payload van
 * tienduizenden bytes de stack laten overlopen, vandaar de lus.
 */
function toBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function encode(applicationId: string, history: ChatMessage[], question: string): ChatRequest {
  return {
    application_id: applicationId,
    history: toBase64(JSON.stringify(history)),
    question: toBase64(question),
  };
}

function bodyBytes(payload: ChatRequest): number {
  return new TextEncoder().encode(JSON.stringify(payload)).length;
}

/**
 * Bouwt de request-body voor `POST /chat`.
 *
 * `messages` is de onveranderde uitkomst van `buildRequestMessages`: strikt
 * alternerend, beginnend bij de gebruiker en eindigend op de nieuwe
 * gebruikersbeurt. Het laatste bericht wordt daarom `question` en de rest
 * `history`.
 *
 * Bij een te grote body vallen de oudste beurten weg tot het past. Dat raakt
 * alleen wat verstuurd wordt — de gerenderde conversatie blijft volledig.
 */
export function buildChatPayload(applicationId: string, messages: ChatMessage[]): ChatRequest {
  const question = messages[messages.length - 1]?.content ?? '';
  const history = messages.slice(0, -1);

  let payload = encode(applicationId, history, question);

  while (
    history.length > 0 &&
    (bodyBytes(payload) > MAX_BODY_BYTES || history.length > MAX_HISTORY_MESSAGES)
  ) {
    // Laat een volledig vraag-antwoordpaar vallen, zodat de alternering intact
    // blijft. Zelfde regel als buildRequestMessages.ts:32-34.
    history.shift();
    while (history[0]?.role === 'assistant') history.shift();
    payload = encode(applicationId, history, question);
  }

  return payload;
}
